import styled, { css, keyframes } from 'styled-components';
import Input from '@components/input';
import Button from '@components/button';
import { wrapperWidths } from '@components/wrapper.styled';
import Text from '@components/text';
import { generateScales } from '@helpers/generate-media-queries';
import { LargeButton } from '@styles/button.styled';

export const HubSpotForm = styled.div`
  ${({ theme }) => css`
    width: 100%;
    .hs-form {
      display: flex;
      flex-direction: column;
      justify-content: flex-start;
      align-items: flex-start;
      row-gap: 1rem;
      column-gap: 1rem;
      width: 100%;
    }

    .hs_submit {
      width: 100%;
    }

    .actions,
    .hs_recaptcha {
      width: 100%;
      display: flex;
      justify-content: flex-start;
      align-items: flex-start;
      width: 100%;
    }

    .hs-button {
      ${LargeButton}
      font-weight: 400;
      border-color: ${theme.colors.primary};
      background: ${theme.colors.primary};
      color: ${theme.colors.white};
      min-width: 14rem;

      :hover {
        border-color: ${theme.colors.black};
        background: ${theme.colors.black};
        color: ${theme.colors.white};
      }

      :focus {
        outline: 4px solid ${theme.colors.white}44;
      }
    }

    .form-columns-1 {
      display: flex;
      flex-direction: column;
      row-gap: 1.5rem;
      column-gap: 1.5rem;
      width: 100%;
      max-width: unset;
    }

    .form-columns-2 {
      display: flex;
      flex-direction: row;
      row-gap: 1.5rem;
      column-gap: 1.5rem;
      width: 100%;
      max-width: unset;

      @media only screen and (max-width: ${theme.breakpoints.md}) {
        flex-direction: column;
      }
    }

    .hs-form-field {
      display: flex;
      flex-direction: column;
      row-gap: 0.0625rem;
      width: 100%;
      max-width: unset;

      .input {
        margin-right: 0 !important;
      }

      input,
      select {
        border-radius: 0.125rem;
        ${generateScales(
          'font-size',
          theme.inputSize.lg.mobile.fontSize,
          theme.inputSize.lg.desktop.fontSize,
          theme
        )}
        ${generateScales('padding-left', '0.5rem', '0.75rem', theme)}
        ${generateScales('padding-right', '0.5rem', '0.75rem', theme)}
        ${generateScales('height', '2.75rem', '3rem', theme)}

        border: 1px solid ${theme.colors.white};
        background-color: ${theme.colors.black};
        color: ${theme.colors.white};
        outline: none;
        font-family: ${theme.fontFamily.text};
        transition: all ${theme.transitions.normal};
        border-radius: 0.125rem;
        width: 100% !important;

        ::placeholder {
          color: ${theme.colors.dark}99;
        }

        :focus {
          border-color: ${theme.colors.primary};
          outline: 4px solid ${theme.colors.secondary}22;
        }
      }

      textarea {
        border-radius: 0.125rem;
        ${generateScales(
          'font-size',
          theme.inputSize.lg.mobile.fontSize,
          theme.inputSize.lg.desktop.fontSize,
          theme
        )}
        ${generateScales('padding-left', '0.5rem', '0.75rem', theme)}
        ${generateScales('padding-right', '0.5rem', '0.75rem', theme)}
        ${generateScales('height', '8.25rem', '9rem', theme)}

        border: 1px solid ${theme.colors.darkPurple};
        background-color: ${theme.colors.white};
        color: ${theme.colors.darkPurple};
        outline: none;
        font-family: ${theme.fontFamily.text};
        transition: all ${theme.transitions.normal};
        border-radius: 0.125rem;
        width: 100% !important;
        max-width: 40rem;

        ::placeholder {
          color: ${theme.colors.dark}99;
        }

        :focus {
          border-color: ${theme.colors.dark};
          outline: 4px solid ${theme.colors.secondary}22;
        }
      }

      label {
        font-weight: 400;
        font-family: ${theme.fontFamily.text};
        font-size: ${theme.fontSize.extraSmall.desktop};
        line-height: ${theme.lineHeights.xs};
        color: ${theme.colors.white};
        margin: 0 0 ${theme.spacing.xs} 0;

        &.hs-error-msg {
          margin-top: 0.5rem;
          font-family: ${theme.fontFamily.additional};
          color: red;
          font-size: ${theme.fontSize.extraSmall.desktop};
        }
      }
    }
  `}
`;

interface NewsletterWrapperProps {
  isFixed: boolean;
  offsetAmount: number;
}

const hideModal = keyframes`
  0% {
    opacity: 1;
    transform: translateY(0) translateX(-50%);
  }
  100% {
    opacity: 0;
    transform: translateY(100%) translateX(-50%);
  }
`;

export const NewsletterWrapper = styled.div<NewsletterWrapperProps>`
  ${({ theme, isFixed, offsetAmount = 96 }) => css`
    width: 100%;
    ${isFixed &&
    css`
      position: fixed;
      bottom: ${offsetAmount > 0 ? `calc(${offsetAmount}px + 2rem)` : '1rem'};
      left: 50%;
      transform: translateX(-50%);
      z-index: 101;
      width: ${wrapperWidths.xxl};
      padding: 0;

      &.animate-out {
        animation: ${hideModal} 0.5s ease-in-out;
      }

      @media only screen and (max-width: ${theme.breakpoints.xxl}) {
        width: ${wrapperWidths.xxl};
      }

      @media only screen and (max-width: ${theme.breakpoints.xl}) {
        width: ${wrapperWidths.xl};
      }

      @media only screen and (max-width: ${theme.breakpoints.lg}) {
        width: ${wrapperWidths.lg};
      }

      @media only screen and (max-width: ${theme.breakpoints.md}) {
        position: unset;
        bottom: unset;
        left: unset;
        transform: unset;
        z-index: unset;
        width: unset;
        padding: unset;

        &.animate-out {
          animation: none;
        }
      }
    `}
  `}
`;

export const NewsletterTitle = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    row-gap: 0.375rem;

    @media only screen and (max-width: ${theme.breakpoints.md}) {
      h1,
      h2,
      h3,
      h4 {
        color: ${theme.colors.primary};
      }
    }
  `}
`;

export const NewsletterHeader = styled.div`
  display: flex;
  flex-direction: row;
  column-gap: 1rem;
  align-items: flex-start;
`;

export const CloseButton = styled.button`
  ${({ theme }) => css`
    display: block;
    height: 1.75rem;
    width: 1.75rem;
    position: relative;
    color: ${theme.colors.muted};
    border: none;
    background-color: ${theme.backgroundColors.light};
    border-radius: 50%;
    cursor: pointer;
    position: absolute;
    top: 2rem;
    right: 2rem;
    z-index: 2;

    @media only screen and (max-width: ${theme.breakpoints.lg}) {
      right: 1.5rem;
    }

    @media only screen and (max-width: ${theme.breakpoints.sm}) {
      right: 1rem;
    }

    :hover {
      color: ${theme.colors.white};
      background-color: ${theme.colors.secondary};
    }

    ::before {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      width: 0.875rem;
      height: 2px;
      border-radius: 1px;
      transform-origin: 0 0;
      background-color: currentColor;
      -webkit-transform: rotate(-45deg);
      transform: rotate(-45deg) translate(-50%, -50%);
    }

    ::after {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      transform-origin: 0 0;
      width: 0.875rem;
      height: 2px;
      border-radius: 1px;
      background-color: currentColor;
      -webkit-transform: rotate(45deg);
      transform: rotate(45deg) translate(-50%, -50%);
    }
  `}
`;

export const TopButton = styled.div`
  ${({ theme }) => css`
    position: absolute;
    top: 2rem;
    right: 2rem;

    @media only screen and (max-width: ${theme.breakpoints.lg}) {
      right: 1.5rem;
    }

    @media only screen and (max-width: ${theme.breakpoints.sm}) {
      right: 1rem;
    }
  `}
`;

export const NewsletterContainer = styled.div`
  ${({ theme }) => css`
    // layout
    display: flex;
    flex-direction: column;
    row-gap: 1.75rem;
    position: relative;
    overflow: hidden;
    width: 100%;

    // styling

    @media only screen and (max-width: ${theme.breakpoints.lg}) {
      max-width: 100%;
    }

    h3 {
      margin-bottom: 0;
      width: 100%;
    }
  `}
`;

export const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  row-gap: 1.75rem;
`;

export const Fields = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 1rem;
`;

export const FormRow = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: row;
    column-gap: 1rem;

    @media only screen and (max-width: ${theme.breakpoints.sm}) {
      flex-direction: column;
      row-gap: 1rem;
    }
  `}
`;

export const StyledLabelText = styled(Text)`
  ${({ theme }) => css`
    font-family: ${theme.fontFamily.display};
  `}
`;

export const StyledInput = styled(Input)<{ isInvalid: boolean }>`
  ${({ theme, isInvalid }) => css`
    width: 100%;
    padding: 0 0.625rem;
    border: 1px solid ${isInvalid ? theme.colors.tertiary : theme.colors.white};
    background-color: ${theme.colors.black};
    color: ${theme.colors.primary};
    max-height: 2.75rem;
    font-size: ${theme.fontSize.small.desktop};

    ::placeholder {
      color: ${theme.colors.black};
    }

    :focus {
      border-color: ${theme.colors.primary};

      ::placeholder {
        color: ${theme.colors.black};
      }
    }

    @media only screen and (max-width: ${theme.breakpoints.sm}) {
      max-height: 2.5rem;
    }
  `}
`;

export const HelperText = styled.div`
  ${({ theme }) => css`
    font-family: ${theme.fontFamily.additional};
    color: ${theme.colors.tertiary};
    font-size: ${theme.fontSize.extraSmall.desktop};
    margin-top: 0.375rem;
  `}
`;

export const InputContainer = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 0.625rem;
  width: 100%;
`;

export const ConfirmationWrapper = styled.div`
  ${({ theme }) => css`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    align-content: center;
    row-gap: 0.25rem;
    position: absolute;
    top: 0;
    left: 0;
    bottom: 0;
    right: 0;
    background-color: ${theme.backgroundColors.white};
    padding: 2rem;

    @media only screen and (max-width: ${theme.breakpoints.lg}) {
      padding: 2rem 1.5rem;
    }

    @media only screen and (max-width: ${theme.breakpoints.sm}) {
      padding: 2rem 1rem;
    }
  `}
`;

export const StyledButton = styled(Button)`
  ${({ theme }) => css`
    background-color: ${theme.colors.primary};
    border-color: ${theme.colors.primary};
    color: ${theme.colors.black};

    :hover {
      background-color: ${theme.colors.white};
      border-color: ${theme.colors.white};
      color: ${theme.colors.dark};
    }
  `}
`;
